import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query, QueryGetVisitorGroupsForTenantArgs } from 'graphql/generated/graphqlTypes';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import { isVisitorWithoutGroup } from 'utils/Visitor/visitor';
import { isScheduleSetUpForVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import { filterHiddenVisitorGroups } from 'v2/utils/visitorGroupOrigin';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// Only ids are selected for the members and access grant locations/doors, so read `memberIds`, `locationIds` and
// `doorIds` from these groups instead of `members`, `locations` and `doors`.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
// The visitors only hold the id of each group they belong to, so their group types are looked up from the groups.
const GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;

const useDashboardTodo = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  const {
    loading: deviceGroupsLoading,
    error: deviceGroupsError,
    data: deviceGroupsData
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (deviceGroupsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(deviceGroupsError);
    }
  }, [deviceGroupsError]);

  const {
    loading: visitorsLoading,
    error: visitorsError,
    data: visitorsData
  } = useQuery(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (visitorsError) {
      handleFetchError(translation.users_error_fetching);
      console.error(visitorsError);
    }
  }, [visitorsError]);

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData
  } = useQuery(GET_DOORS_FOR_TENANT_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  // true only on the very first fetch, before any of the four queries have returned data
  const loadingDashboardTodo =
    (visitorGroupsLoading && !visitorGroupsData) ||
    (deviceGroupsLoading && !deviceGroupsData) ||
    (visitorsLoading && !visitorsData) ||
    (doorsLoading && !doorsData);

  // true when all four queries have cached data and at least one of them is mid-refetch. `loading` is used instead of
  // the network status because a failed refetch leaves the status on `error`, which would keep the "refreshing"
  // indicator on until the next success.
  const isRefetchingDashboardTodo =
    !loadingDashboardTodo &&
    ((!!visitorGroupsData && visitorGroupsLoading) ||
      (!!deviceGroupsData && deviceGroupsLoading) ||
      (!!visitorsData && visitorsLoading) ||
      (!!doorsData && doorsLoading));

  useRegisterBackgroundRefetch(isRefetchingDashboardTodo);

  const visitorGroups = useMemo(
    () => filterHiddenVisitorGroups(visitorGroupsData?.getVisitorGroupsForTenant ?? []),
    [visitorGroupsData]
  );

  const deviceGroups = useMemo(() => deviceGroupsData?.getLocationsForTenant ?? [], [deviceGroupsData]);

  // The group types come from the unfiltered groups: the hidden Administrators and AtlasInternal memberships must
  // stay recognisable, otherwise users who only belong to those groups would no longer count as without a group.
  const visitors = useMemo(() => {
    const groupTypeById = new Map(
      (visitorGroupsData?.getVisitorGroupsForTenant ?? []).map((group) => [group.id, group.type])
    );

    return (visitorsData?.getVisitors ?? []).map((visitor) => ({
      ...visitor,
      visitorGroupMembership: visitor.visitorGroupMembership.map((membership) => ({
        ...membership,
        visitorGroup: { ...membership.visitorGroup, type: groupTypeById.get(membership.visitorGroup.id) }
      }))
    }));
  }, [visitorsData, visitorGroupsData]);

  const doors = useMemo(() => doorsData?.getDoorsForTenant ?? [], [doorsData]);

  // Without the groups the group types are unknown, so no user is classified from incomplete data. Like the other counts
  // of a failed request this is 0, which hides the row on the dashboard. A loaded but empty groups list is valid data.
  const usersWithoutGroup = useMemo(
    () => (visitorGroupsData ? visitors.filter(isVisitorWithoutGroup).length : 0),
    [visitors, visitorGroupsData]
  );

  const userGroupsWithoutSchedule = useMemo(
    () => visitorGroups.filter((group) => !isScheduleSetUpForVisitorGroup(group)).length,
    [visitorGroups]
  );

  const userGroupsWithoutDevices = useMemo(
    () =>
      visitorGroups.filter(
        (group) => !group.accessGrants.some((grant) => grant.doorIds.length > 0 || grant.locationIds.length > 0)
      ).length,
    [visitorGroups]
  );

  const userGroupsWithoutUsers = useMemo(
    () => visitorGroups.filter((group) => group.memberIds.length === 0).length,
    [visitorGroups]
  );

  const deviceGroupsWithoutDevices = useMemo(
    () => deviceGroups.filter((location) => location.doors.length === 0).length,
    [deviceGroups]
  );

  const deviceGroupsWithoutUserGroups = useMemo(
    () =>
      deviceGroups.filter(
        (location) =>
          !visitorGroups.some((group) => group.accessGrants.some((grant) => grant.locationIds.includes(location.id)))
      ).length,
    [deviceGroups, visitorGroups]
  );

  return {
    totalUsers: visitors.length,
    totalUserGroups: visitorGroups.length,
    totalDevices: doors.length,
    totalDeviceGroups: deviceGroups.length,
    usersWithoutGroup,
    userGroupsWithoutSchedule,
    userGroupsWithoutDevices,
    userGroupsWithoutUsers,
    deviceGroupsWithoutDevices,
    deviceGroupsWithoutUserGroups,
    loadingDashboardTodo,
    isRefetchingDashboardTodo
  };
};

export default useDashboardTodo;
